import {Injectable} from '@angular/core'

import {AppConfigService} from '@app/app-config.service'
import {LoggerService} from '@app/core/services/logger.service'
import {
  rememberSsoLoginError,
  resetSsoRetryAttempts,
} from '@app/core/util/sso-login-error.util'

/** sessionStorage keys for the in-flight PKCE handshake. */
const PKCE_VERIFIER_KEY = 'sso.pkce.verifier'
const STATE_KEY = 'sso.state'

/** Fixed reason codes understood by the custom Keycloak login theme. */
export type SsoLoginReason = 'session_expired' | 'login_retry' | 'login_failed'

/**
 * A token-refresh attempt whose outcome is unknown — the network dropped, or
 * Keycloak answered 5xx/timeout. The refresh token may well still be valid,
 * so callers should retry instead of ending the session. A definitive refusal
 * (Keycloak answered 4xx: the refresh token is consumed or expired) is
 * reported as `null`, never as this error.
 */
export class SsoTransientRefreshError extends Error {
  constructor(detail: string) {
    super(`Keycloak token refresh did not complete: ${detail}`)
  }
}

/**
 * The tokens Keycloak hands back from the token endpoint. `accessToken` is the
 * API credential; `refreshToken` renews it silently before it expires;
 * `idToken` is the `id_token_hint` that lets logout skip Keycloak's
 * confirmation page.
 */
export interface SsoTokens {
  accessToken: string
  refreshToken?: string
  idToken?: string
}

/**
 * Keycloak/OIDC login from the SPA's point of view.
 *
 * Two shapes are supported, picked by what's configured:
 *
 *  - **BFF wrapper** (`ssoLoginUrl`): redirect verbatim to a backend entry
 *    point that drives Keycloak server-side and redirects back with the token
 *    already in the URL. {@link captureTokenFromUrl} picks it up.
 *
 *  - **Direct Authorization Code + PKCE** (`ssoAuthorizationEndpoint` +
 *    `ssoClientId`, standard flow with PKCE enabled on the client): we redirect
 *    the browser to Keycloak's authorization endpoint with `response_type=code`
 *    and a SHA-256 `code_challenge`, then — on the way back — exchange the
 *    returned `code` (plus the stored `code_verifier`) at the token endpoint for
 *    the JWT. This replaces the old implicit flow (`response_type=token`), which
 *    is deprecated in OAuth 2.1; it needs the token endpoint to be reachable
 *    from the browser and CORS ("Web Origins") to allow this SPA's origin.
 *
 * Either way the captured access token is sent as `Authorization: Bearer <jwt>`;
 * the core API maps its username claim to the CURO user. Turning the token into
 * a session (context + permissions) is {@link AuthenticationService}'s job.
 */
@Injectable({providedIn: 'root'})
export class SsoAuthService {
  constructor(
    private config: AppConfigService,
    private logger: LoggerService,
  ) {}

  /** True when enough is configured to attempt an SSO login (controls UI). */
  get isEnabled(): boolean {
    return (
      !!this.config.ssoLoginUrl ||
      !!(this.config.ssoAuthorizationEndpoint && this.config.ssoClientId)
    )
  }

  /** Send the browser off to start the login. */
  async startLogin(forceLogin = false, reason?: SsoLoginReason): Promise<void> {
    // Prefer a backend wrapper when present.
    if (this.config.ssoLoginUrl) {
      window.location.replace(this.config.ssoLoginUrl)
      return
    }
    if (this.config.ssoAuthorizationEndpoint && this.config.ssoClientId) {
      window.location.replace(await this.buildCodeAuthUrl(forceLogin, reason))
      return
    }
    this.logger.log(
      'SSO login requested but not configured (need ssoLoginUrl, or ' +
        'ssoAuthorizationEndpoint + ssoClientId)',
    )
  }

  /**
   * Complete a post-login redirect, returning the raw JWT or `null`.
   *
   * - **Code + PKCE:** when the URL carries `?code=…`, validate `state`, POST
   *   the code + stored `code_verifier` to the token endpoint, and return the
   *   `access_token`. The URL is scrubbed either way.
   * - **BFF / legacy:** otherwise fall back to {@link captureTokenFromUrl},
   *   which reads a token Keycloak/the wrapper left directly in the URL.
   *
   * Async because the code exchange is a network call — the app initializer in
   * `main.ts` awaits it before the router runs.
   */
  async handleRedirectCallback(): Promise<SsoTokens | null> {
    // The OAuth code/state come back in the real query string (redirect_uri is
    // `origin + '/'`), NOT the hash fragment — so anchor on `location.search`.
    // This avoids mistaking a hash-route filter param (e.g.
    // `#/transactions?code=00`) for an OAuth callback and scrubbing the route.
    const search = window.location.search
    const errorMatch = search.match(/[?&]error=([^&]+)/)
    if (errorMatch && this.config.ssoAuthorizationEndpoint) {
      /* Keycloak reported a protocol error by redirecting back with
         `?error=…` (RFC 6749 §4.1.2.1 — with a trusted redirect_uri the
         client owns the error UX; Keycloak never shows these on its own
         pages). Remember it as retryable: the login page restarts the flow
         with fresh state, which the still-valid Keycloak SSO cookie usually
         completes silently. */
      const error = this.decodeParam(errorMatch[1])
      const descMatch = search.match(/[?&]error_description=([^&]+)/)
      const description = descMatch ? this.decodeParam(descMatch[1]) : ''
      // The one-shot handshake state belongs to the failed attempt.
      sessionStorage.removeItem(STATE_KEY)
      sessionStorage.removeItem(PKCE_VERIFIER_KEY)
      this.scrubTokenFromUrl()
      const message = `Keycloak sign-in was not completed (${error})${description ? `: ${description}` : ''}`
      rememberSsoLoginError(message, true)
      this.logger.log(message)
      return null
    }
    const codeMatch = search.match(/[?&]code=([^&]+)/)
    if (codeMatch && this.config.ssoAuthorizationEndpoint) {
      const code = decodeURIComponent(codeMatch[1])
      const stateMatch = search.match(/[?&]state=([^&]+)/)
      const returnedState = stateMatch
        ? decodeURIComponent(stateMatch[1])
        : null
      return this.exchangeCodeForToken(code, returnedState)
    }
    // BFF / legacy path: a bare token in the URL, no refresh/id token.
    const token = this.captureTokenFromUrl()
    return token ? {accessToken: token} : null
  }

  /**
   * Build a Keycloak Authorization-Code+PKCE authorization URL. Persists the
   * per-request `code_verifier` and `state` in sessionStorage so
   * {@link exchangeCodeForToken} can complete the handshake on return.
   */
  private async buildCodeAuthUrl(
    forceLogin: boolean,
    reason?: SsoLoginReason,
  ): Promise<string> {
    const verifier = this.randomToken(32)
    const challenge = await this.pkceChallenge(verifier)
    const state = this.randomToken(16)
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
    sessionStorage.setItem(STATE_KEY, state)

    const params = new URLSearchParams({
      client_id: this.config.ssoClientId,
      redirect_uri: this.redirectUri(),
      response_type: 'code',
      scope: this.config.ssoScope || 'openid',
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    })
    if (forceLogin) {
      params.set('prompt', 'login')
    }
    if (reason) {
      // Pass a fixed code, never arbitrary display text. The Keycloak theme
      // owns the localized message and ignores unknown values.
      params.set('portal_reason', reason)
    }
    return `${this.config.ssoAuthorizationEndpoint}?${params.toString()}`
  }

  /**
   * Exchange an authorization `code` for a JWT at the token endpoint using the
   * stored PKCE `code_verifier`. Returns the `access_token`, or `null` on any
   * mismatch/failure. Always clears the one-shot verifier/state and scrubs the
   * URL first so a reload can't replay the callback.
   */
  private async exchangeCodeForToken(
    code: string,
    returnedState: string | null,
  ): Promise<SsoTokens | null> {
    const expectedState = sessionStorage.getItem(STATE_KEY)
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
    sessionStorage.removeItem(STATE_KEY)
    sessionStorage.removeItem(PKCE_VERIFIER_KEY)
    this.scrubTokenFromUrl()

    if (!verifier) {
      /* Retryable: the verifier lives in per-tab sessionStorage, so this is
         typically a callback that landed in a different tab than the one that
         started the login, or a replayed callback URL. */
      rememberSsoLoginError(
        'Keycloak returned an authorization code, but the PKCE verifier was missing. Please retry the login.',
        true,
      )
      this.logger.log(
        'SSO code callback with no stored PKCE verifier — ignored',
      )
      return null
    }
    // Guard against CSRF / stray callbacks: the returned state must match.
    if (!expectedState || returnedState !== expectedState) {
      // Retryable: usually a back-button replay of a stale Keycloak page.
      rememberSsoLoginError(
        'Keycloak login state validation failed. Please retry the login.',
        true,
      )
      this.logger.log('SSO state mismatch on callback — ignored')
      return null
    }

    const configuredEndpoint = this.tokenEndpoint()
    if (!configuredEndpoint) {
      rememberSsoLoginError('The Keycloak token endpoint is not configured.')
      this.logger.log(
        'SSO code flow needs a token endpoint (ssoTokenEndpoint, or a ' +
          'derivable authorization endpoint) — none available',
      )
      return null
    }
    const endpoint = configuredEndpoint

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri(),
      client_id: this.config.ssoClientId,
      code_verifier: verifier,
    })

    try {
      /* Plain `fetch` on purpose: the token endpoint is a different origin
         (Keycloak) and must NOT carry the CURO API interceptors' Authorization
         header. Form-encoded per the OAuth token-request spec. */
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
      })
      if (!res.ok) {
        const details = await this.tokenErrorDescription(res)
        const message = `Keycloak token exchange failed (${res.status})${details ? `: ${details}` : ''}`
        // Retryable: an expired/replayed code — a fresh flow mints a new one.
        rememberSsoLoginError(message, true)
        this.logger.log(message)
        return null
      }
      const json = (await res.json()) as {
        access_token?: string
        refresh_token?: string
        id_token?: string
      }
      if (!json.access_token) {
        const message =
          'Keycloak completed the token exchange, but returned no access token.'
        rememberSsoLoginError(message, true)
        this.logger.log(message)
        return null
      }
      this.logger.log('SSO token exchange succeeded')
      // The sign-in round-trip worked: refill the automatic-retry budget.
      resetSsoRetryAttempts()
      // The access token is the credential the backend validates on every
      // request; the refresh token renews it, the id token hints logout.
      return {
        accessToken: json.access_token,
        refreshToken: json.refresh_token,
        idToken: json.id_token,
      }
    } catch (e) {
      rememberSsoLoginError(
        'The browser could not exchange the Keycloak authorization code for a token.',
        true,
      )
      this.logger.log('SSO token exchange error', e)
      return null
    }
  }

  /** Decode an OAuth callback param, tolerating `+`-encoded spaces. */
  private decodeParam(raw: string): string {
    try {
      return decodeURIComponent(raw.replace(/\+/g, '%20'))
    } catch {
      return raw
    }
  }

  /**
   * Renew the access token from a Keycloak refresh token (OAuth
   * `grant_type=refresh_token`). Keycloak rotates the refresh token, so the
   * caller must persist the returned one. Returns `null` only when Keycloak
   * definitively refused the refresh token (expired or consumed — the SSO
   * session ended), so the caller should fall back to a fresh login. Throws
   * {@link SsoTransientRefreshError} when the outcome is unknown (network
   * failure, 5xx) — the caller should retry, NOT end the session. Same
   * plain-`fetch`/proxy path as {@link exchangeCodeForToken} to stay off the
   * CURO API interceptors.
   */
  async refreshAccessToken(refreshToken: string): Promise<SsoTokens | null> {
    const configuredEndpoint = this.tokenEndpoint()
    if (!configuredEndpoint || !refreshToken) {
      return null
    }
    const endpoint = configuredEndpoint
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: this.config.ssoClientId,
    })
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
      })
      if (!res.ok) {
        this.logger.log(`SSO token refresh failed (${res.status})`)
        /* 4xx (invalid_grant & co) = Keycloak looked at the refresh token and
           said no — definitive. 5xx/timeout-ish = Keycloak itself hiccuped;
           the session may well still be alive. */
        if (res.status >= 500 || res.status === 408 || res.status === 429) {
          throw new SsoTransientRefreshError(`HTTP ${res.status}`)
        }
        return null
      }
      const json = (await res.json()) as {
        access_token?: string
        refresh_token?: string
        id_token?: string
      }
      if (!json.access_token) {
        return null
      }
      return {
        accessToken: json.access_token,
        refreshToken: json.refresh_token,
        idToken: json.id_token,
      }
    } catch (e) {
      if (e instanceof SsoTransientRefreshError) {
        throw e
      }
      // fetch threw: offline, DNS, CORS — outcome unknown.
      this.logger.log('SSO token refresh error', e)
      throw new SsoTransientRefreshError(
        e instanceof Error ? e.message : 'network error',
      )
    }
  }

  /**
   * Keycloak RP-initiated logout URL, or `null` when no end-session endpoint
   * can be derived. A full-page navigation here clears Keycloak's own SSO
   * cookie (local token removal alone does not — the next login would return
   * silently as the same user) and then bounces back to
   * {@link redirectUri}. Passing the `id_token_hint` lets Keycloak skip its
   * "Do you want to log out?" confirmation page.
   */
  buildLogoutUrl(idTokenHint?: string | null): string | null {
    const endSession = this.endSessionEndpoint()
    if (!endSession) {
      return null
    }
    const params = new URLSearchParams()
    if (idTokenHint) {
      params.set('id_token_hint', idTokenHint)
    }
    if (this.config.ssoClientId) {
      params.set('client_id', this.config.ssoClientId)
    }
    params.set('post_logout_redirect_uri', this.redirectUri())
    return `${endSession}?${params.toString()}`
  }

  /** The OIDC end-session endpoint, derived from the `…/auth` endpoint. */
  private endSessionEndpoint(): string | null {
    const auth = this.config.ssoAuthorizationEndpoint
    if (auth && auth.endsWith('/auth')) {
      return `${auth.slice(0, -'/auth'.length)}/logout`
    }
    return null
  }

  private async tokenErrorDescription(response: Response): Promise<string> {
    try {
      const body = (await response.json()) as {error_description?: unknown}
      return typeof body.error_description === 'string'
        ? body.error_description
        : ''
    } catch {
      return ''
    }
  }

  /**
   * Where Keycloak sends the browser back to. Must be registered as a valid
   * redirect URI on the Keycloak client AND match byte-for-byte between the
   * authorization request and the token exchange. The portal uses hash routing,
   * so the returned params are picked up at startup before the router runs.
   */
  private redirectUri(): string {
    return window.location.origin + '/'
  }

  /** The OIDC token endpoint: explicit config, else derived from `…/auth`. */
  private tokenEndpoint(): string | null {
    if (this.config.ssoTokenEndpoint) {
      return this.config.ssoTokenEndpoint
    }
    const auth = this.config.ssoAuthorizationEndpoint
    if (auth && auth.endsWith('/auth')) {
      return `${auth.slice(0, -'/auth'.length)}/token`
    }
    return null
  }

  /** URL-safe random token of `bytes` bytes (base64url, no padding). */
  private randomToken(bytes: number): string {
    const arr = new Uint8Array(bytes)
    crypto.getRandomValues(arr)
    return this.base64UrlEncode(arr)
  }

  /** S256 PKCE challenge: base64url( SHA-256( verifier ) ). */
  private async pkceChallenge(verifier: string): Promise<string> {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(verifier),
    )
    return this.base64UrlEncode(new Uint8Array(digest))
  }

  private base64UrlEncode(bytes: Uint8Array): string {
    let binary = ''
    for (const b of bytes) {
      binary += String.fromCharCode(b)
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  }

  /**
   * Pull a JWT directly out of the redirect URL (BFF wrapper / legacy implicit
   * responses), if present, and strip it from the address bar so it isn't left
   * in history or re-processed on reload.
   *
   * Tolerant of where the param lands — the fragment (`#access_token=`,
   * `#token=`) or the query string (`?token=`), with or without a leading hash
   * route — and of the parameter name (`access_token` / `id_token` / `token`).
   * Returns the raw token, or `null` if none was found.
   */
  captureTokenFromUrl(): string | null {
    const href = window.location.href
    const match = href.match(/[#?&](?:access_token|id_token|token)=([^#?&]+)/)
    if (!match) {
      return null
    }
    const token = decodeURIComponent(match[1])
    this.scrubTokenFromUrl()
    return token
  }

  /**
   * Reset the URL to a clean hash route (`#/`) without a reload, dropping the
   * token/code from the visible URL and the back/forward history.
   */
  private scrubTokenFromUrl(): void {
    try {
      /* Drop the query string too — the code flow returns `?code=&state=` there
         (and BFF/implicit may use `?token=`); once captured it must not linger. */
      const clean = window.location.pathname + '#/'
      window.history.replaceState(null, '', clean)
    } catch (e) {
      this.logger.log('Failed to scrub SSO params from URL', e)
    }
  }
}
